"use client";

import useEmblaCarousel from "embla-carousel-react";
import {
    Children,
    cloneElement,
    FunctionComponent,
    isValidElement,
    PropsWithChildren,
    ReactNode,
    useCallback,
    useEffect,
    useState,
    useRef,
} from "react";
import { Bullet, Bullets, Content, NavigationButton, ScrollContainer, ScrollContainerInner, Wrapper } from "./slider.styles";
import { type SlideProps } from "./slide/slide";
import { getCurrentBreakpoint } from "./slider.utils";
import AutoHeight from "embla-carousel-auto-height";
import { SliderController } from "./slider.controller";
import { Button } from "../button/button";

type Breakpoint = {
    slidesPerView?: number;
    spaceBetween?: number;
    scrollStep?: number;
    space?: number;
};

export type Breakpoints = {
    [key: number]: Breakpoint;
};

export interface SliderProps {
    /**
     * Space between slides in px.
     */
    spaceBetween?: number;

    /**
     * A css class which will be added to the slides.
     */
    slideClass?: string;

    /**
     * A css class which will be added to the currently active slide.
     */
    activeSlideClass?: string;

    /**
     * A css class which will be added to all slides which are currently visible.
     */
    activeGroupClass?: string;

    /**
     * Modify some settings in certain breakpoints.
     */
    breakpoints?: Breakpoints;

    /**
     * Slides per view.
     */
    slidesPerView?: number;

    /**
     * If you have slides with eg. shadows or elements which should not cut off due to the slider overflow hidden, you can add a space in px.
     */
    space?: number;

    /**
     * Defines the number of slides to scroll on click next/prev.
     */
    scrollStep?: number;

    /**
     * Whether the navigation should be rendered or not.
     */
    navigation?: boolean;

    /**
     * Whether the pagination should be rendered or not.
     */
    pagination?: boolean;

    /**
     * Will render custom pagination.
     */
    renderPagination?: (slides: number, activeIndex: number, scrollToSlide: (index: number) => void) => ReactNode;

    /**
     * Will move the slider on hover the navigation buttons.
     */
    previewOffset?: number;

    /**
     * A callback function which will be fired on slide change.
     */
    onChangeSlide?: (index: number) => void;

    /**
     * A callback function which will be fired on click a slide.
     */
    onClickSlide?: (slide: number) => void;

    /**
     * Will replace the prev button.
     */
    prevButton?: ReactNode;

    /**
     * Will replace the next button.
     */
    nextButton?: ReactNode;

    /**
     * Height will be automatically adjusted. Will only be applied if `slidesPerView` is equal to `1`
     */
    autoHeight?: boolean;

    /**
     * Alignment of active slide in viewport
     */
    align?: "start" | "center" | "end";

    /**
     * Controller instance to synchronize multiple sliders
     */
    controller?: SliderController;

    /**
     * Class name of the slider.
     */
    className?: string;
}

export const Slider: FunctionComponent<PropsWithChildren<SliderProps>> = ({
    slideClass,
    activeSlideClass,
    activeGroupClass,
    slidesPerView: slidesPerViewDefault = 1,
    spaceBetween: spaceBetweenDefault = 0,
    navigation = true,
    pagination = false,
    scrollStep: scrollStepDefault = 1,
    breakpoints,
    previewOffset = 0,
    renderPagination,
    onChangeSlide,
    onClickSlide,
    space: spaceDefault = 0,
    prevButton,
    nextButton,
    align = "start",
    className,
    autoHeight = false,
    controller,
    children,
}) => {
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [slidesInView, setSlidesInView] = useState<number[]>([]);
    const [prevButtonDisabled, setPrevButtonDisabled] = useState<boolean>(true);
    const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(true);
    const [isHovering, setIsHovering] = useState<boolean>(false);
    const [isClient, setIsClient] = useState<boolean>(false);
    const [controllerActiveSlide, setControllerActiveSlide] = useState<number>(0);
    const cleanupRef = useRef<(() => void) | null>(null);
    const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const currentBreakpoint = getCurrentBreakpoint(breakpoints);
    const slidesPerView = currentBreakpoint?.slidesPerView || slidesPerViewDefault;
    const spaceBetween = currentBreakpoint?.spaceBetween || spaceBetweenDefault;
    const scrollStep = currentBreakpoint?.scrollStep || scrollStepDefault;
    const space = currentBreakpoint?.space || spaceDefault;
    const emblaOptions: any = {
        slidesToScroll: scrollStep,
        containScroll: "trimSnaps",
        align: align,
        duration: 25,
    };
    const plugins = [];
    if (autoHeight) {
        plugins.push(AutoHeight());
    }
    const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions, plugins);
    const modifiedChildren = Children.map(children, (child, index) => {
        if (isValidElement<SlideProps>(child)) {
            const isActive = controller ? index === controllerActiveSlide : index === activeIndex;
            const isInViewport = slidesInView.includes(index);
            return isClient
                ? cloneElement(child as React.ReactElement, {
                      slideClass,
                      activeSlideClass,
                      activeGroupClass,
                      slidesPerView,
                      spaceBetween,
                      space,
                      isActive,
                      isInViewport,
                      onClick: onClickSlide
                          ? () => {
                                if (controller) {
                                    controller.setActiveSlide(index);
                                }
                                onClickSlide(index);
                            }
                          : undefined,
                  })
                : child;
        }
        return child;
    });
    const slides = modifiedChildren?.length || 0;

    const handleMouseEnter = useCallback(
        (direction: "prev" | "next") => {
            if (!emblaApi || previewOffset === 0 || isHovering) return;
            setIsHovering(true);
            const container = emblaApi.rootNode() as HTMLElement;
            const currentTransform = container.style.transform || "translateX(0px)";
            const currentX = currentTransform.includes("translateX")
                ? parseFloat(currentTransform.match(/translateX\((-?\d+(?:\.\d+)?)px\)/)?.[1] || "0")
                : 0;
            const offset = direction === "prev" ? previewOffset : -previewOffset;
            container.style.transition = "transform 500ms";
            container.style.transform = `translateX(${currentX + offset}px)`;
        },
        [emblaApi, previewOffset, isHovering],
    );

    const handleMouseLeave = useCallback(() => {
        if (!emblaApi || !isHovering) return;
        setIsHovering(false);
        const container = emblaApi.rootNode() as HTMLElement;
        container.style.transform = "translateX(0px)";
    }, [emblaApi, isHovering]);

    const scrollPrev = useCallback(() => {
        if (isHovering && emblaApi) {
            const container = emblaApi.rootNode() as HTMLElement;
            if (container) {
                container.style.transform = "translateX(0px)";
            }
            setIsHovering(false);
        }
        if (controller) {
            controller.scrollPrev();
        } else if (emblaApi) {
            emblaApi.scrollPrev();
        }
    }, [emblaApi, controller, isHovering]);

    const scrollNext = useCallback(() => {
        if (isHovering && emblaApi) {
            const container = emblaApi.rootNode() as HTMLElement;
            if (container) {
                container.style.transform = "translateX(0px)";
            }
            setIsHovering(false);
        }
        if (controller && emblaApi) {
            const currentIndex = emblaApi.selectedScrollSnap();
            const maxIndex = emblaApi.scrollSnapList().length - 1;
            const newIndex = Math.min(maxIndex, currentIndex + (scrollStep || 1));
            controller.scrollTo(newIndex);
        } else if (emblaApi) {
            emblaApi.scrollNext();
        }
    }, [emblaApi, controller, scrollStep, isHovering]);

    const scrollTo = useCallback(
        (index: number) => {
            if (controller) {
                controller.scrollTo(index);
            } else if (emblaApi) {
                emblaApi.scrollTo(index);
            }
        },
        [emblaApi, controller],
    );

    const customPrevButton = prevButton
        ? cloneElement(prevButton as React.ReactElement, {
              onClick: scrollPrev,
              onMouseEnter: () => handleMouseEnter("prev"),
              onMouseLeave: handleMouseLeave,
              role: "button",
          })
        : null;

    const customNextButton = nextButton
        ? cloneElement(nextButton as React.ReactElement, {
              onClick: scrollNext,
              onMouseEnter: () => handleMouseEnter("next"),
              onMouseLeave: handleMouseLeave,
              role: "button",
          })
        : null;

    const updateSlidesInView = useCallback(() => {
        if (!emblaApi) return;
        setSlidesInView(emblaApi.slidesInView());
    }, [emblaApi]);

    const updateActiveIndex = useCallback(() => {
        if (!emblaApi) return;
        const newActiveIndex = emblaApi.selectedScrollSnap();
        setActiveIndex(newActiveIndex);
        onChangeSlide?.(newActiveIndex);
    }, [emblaApi, onChangeSlide]);

    const updateButtonStates = useCallback(() => {
        if (!emblaApi) return;
        setPrevButtonDisabled(!emblaApi.canScrollPrev());
        setNextButtonDisabled(!emblaApi.canScrollNext());
    }, [emblaApi]);

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        if (!controller) return;
        return controller.registerActiveSlideCallback(setControllerActiveSlide);
    }, [controller]);

    useEffect(() => {
        if (!emblaApi) return;
        emblaApi.reInit(emblaOptions);
    }, [emblaApi, scrollStep]);

    useEffect(() => {
        if (!emblaApi) return;
        updateActiveIndex();
        updateSlidesInView();
        updateButtonStates();
        emblaApi.on("select", updateActiveIndex);
        emblaApi.on("select", updateButtonStates);
        emblaApi.on("slidesInView", updateSlidesInView);
        emblaApi.on("reInit", updateSlidesInView);
        emblaApi.on("reInit", updateButtonStates);
        if (controller) {
            cleanupRef.current = controller.register(emblaApi);
        }

        return function cleanup() {
            emblaApi.off("select", updateActiveIndex);
            emblaApi.off("select", updateButtonStates);
            emblaApi.off("slidesInView", updateSlidesInView);
            emblaApi.off("reInit", updateSlidesInView);
            emblaApi.off("reInit", updateButtonStates);
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
        };
    }, [emblaApi, updateActiveIndex, updateSlidesInView, updateButtonStates, controller]);

    useEffect(() => {
        return () => {
            if (previewTimeoutRef.current) {
                clearTimeout(previewTimeoutRef.current);
            }
        };
    }, []);

    return (
        <Wrapper className={className}>
            <Content>
                <ScrollContainer ref={emblaRef}>
                    <ScrollContainerInner
                        spaceBetween={spaceBetween}
                        space={space}
                        style={{
                            transition: autoHeight ? "height 500ms cubic-bezier(0.16, 1, 0.3, 1)" : undefined,
                        }}
                    >
                        {modifiedChildren}
                    </ScrollContainerInner>
                </ScrollContainer>
                {navigation && (
                    <>
                        <NavigationButton direction="prev" disabled={prevButtonDisabled}>
                            {customPrevButton || (
                                <Button
                                    rounded="full"
                                    arrow={true}
                                    onClick={scrollPrev}
                                    onMouseEnter={() => handleMouseEnter("prev")}
                                    onMouseLeave={handleMouseLeave}
                                />
                            )}
                        </NavigationButton>
                        <NavigationButton direction="next" disabled={nextButtonDisabled}>
                            {customNextButton || (
                                <Button
                                    rounded="full"
                                    arrow={true}
                                    onClick={scrollNext}
                                    onMouseEnter={() => handleMouseEnter("next")}
                                    onMouseLeave={handleMouseLeave}
                                />
                            )}
                        </NavigationButton>
                    </>
                )}
            </Content>
            {pagination
                ? renderPagination?.(slides, activeIndex, scrollTo) || (
                      <Bullets>
                          {modifiedChildren?.map((_, index) => (
                              <Bullet active={activeIndex === index} onClick={() => scrollTo(index)} role="button" key={index}>
                                  <span></span>
                              </Bullet>
                          ))}
                      </Bullets>
                  )
                : null}
        </Wrapper>
    );
};
